/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable no-console */
import SelectorEngine from '../../../utils/dom/selector-engine';
import Manipulator from '../../../utils/dom/manipulator';
import Request from '../../../utils/request';

const _formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

const TABLE_COLUMNS = [
	{ key: 'startDate', label: 'Start date', sortable: true },
	{ key: 'venueName', label: 'Venue', sortable: true },
	{ key: 'totalBooked', label: 'Booked', sortable: true },
	{ key: 'totalWaiting', label: 'Waiting', sortable: true },
	{ key: 'status', label: 'Status', sortable: true }
];

const _statusTagClass = (status) =>
	status === 'Accepting Bookings'
		? 'lta-tag--success'
		: status === 'Hidden'
		? 'lta-tag--inactive'
		: status === 'Cancelled'
		? 'lta-tag--lta-dark-blue'
		: status === 'Completed'
		? 'lta-tag--success'
		: status === 'In Progress'
		? 'lta-tag--lta-blue'
		: status === 'Awaiting Results'
		? 'lta-tag--danger'
		: 'lta-tag--lta-dark-blue';

const TAB_KEYS = {
	ACTIVE: 'active',
	COMPLETED: 'completed',
	CANCELLED: 'cancelled'
};

const CLASS_FILTER = {
	[TAB_KEYS.ACTIVE]: (a) => a.status !== 'Cancelled' && a.status !== 'Completed',
	[TAB_KEYS.COMPLETED]: (a) => a.status === 'Completed',
	[TAB_KEYS.CANCELLED]: (a) => a.status === 'Cancelled'
};

const TAB_SELECTORS = {
	[TAB_KEYS.ACTIVE]: '.js-course-providers-course-details-active-tab',
	[TAB_KEYS.COMPLETED]: '.js-course-providers-course-details-completed-tab',
	[TAB_KEYS.CANCELLED]: '.js-course-providers-course-details-cancelled-tab'
};

const DEFAULT_FILTERS = {
	startDate: '',
	venue: '',
	booked: '',
	waiting: '',
	status: ''
};

const DEFAULT_SORT = { key: null, dir: 0 };

class CourseProvidersCourseDetails {
	constructor() {
		this._wrapper = SelectorEngine.findOne('.js-course-providers-course-details-wrapper');
		if (!this._wrapper) return;

		this._courseID = new URLSearchParams(window.location.search).get('courseId') || 'course-id-not-found';
		this._courseDetailsApiUrl = Manipulator.getDataAttribute(this._wrapper, 'course-details-api-url');
		this._classDetailsPageUrl = Manipulator.getDataAttribute(this._wrapper, 'class-details-page-url');
		this._wrapperLoading = this._wrapper.querySelector('.js-course-providers-course-details-wrapper-loading');
		this._wrapperEmpty = this._wrapper.querySelector('.js-course-providers-course-details-wrapper-empty');
		this._courseDetailsContainer = this._wrapper.querySelector('.js-course-providers-course-details-container');
		this._courseNameContainer = this._wrapper.querySelector('.js-course-providers-course-details-name');
		this._currentCourse = {};
		this._classes = [];
		this._filters = JSON.parse(JSON.stringify(DEFAULT_FILTERS));

		// Tabs
		this._tabButtons = {
			active: this._wrapper.querySelector('#lta-course-tab-1'),
			completed: this._wrapper.querySelector('#lta-course-tab-2'),
			cancelled: this._wrapper.querySelector('#lta-course-tab-3')
		};
		this._classesSort = JSON.parse(JSON.stringify(DEFAULT_SORT));
		this._activeTab = 'active';
		Object.entries(this._tabButtons).forEach(([key, btn]) => {
			if (btn) btn.addEventListener('click', () => {
				this._activeTab = key;
				this._filters = JSON.parse(JSON.stringify(DEFAULT_FILTERS));
				this._updateFiltersUI();
				this._updateTabTableUI();
			});
		});

		this._fetchCourseDetails();
	}

	_updateCourseInfoUI() {
		if (this._courseNameContainer) {
			this._courseNameContainer.innerHTML = this._currentCourse.name;
		}
	}

	_updateTabCountsUI() {
		const classes = this._classes || [];
		const counts = Object.fromEntries(
			Object.entries(CLASS_FILTER).map(([key, fn]) => [key, classes.filter(fn).length])
		);

		Object.entries(this._tabButtons).forEach(([key, btn]) => {
			if (!btn) return;
			const title = btn.querySelector('.title');
			if (title) title.textContent = `${title.textContent.replace(/\s*\(\d+\)$/, '')} (${counts[key]})`;
		});
	}

	_updateFiltersUI() {
		const tabContainer = this._wrapper.querySelector(TAB_SELECTORS[this._activeTab]);
		const filtersContainer = tabContainer.querySelector('.js-classes-filters');
		if (!filtersContainer) return;

		const allTabClasses = this._getTabClasses();

		const statusOptions = [...new Set((allTabClasses || []).map((c) => c.status).filter(Boolean))]
			.map((s) => `<option value="${s}" ${this._filters.status === s ? 'selected' : ''}>${s}</option>`)
			.join('');
		const startDateOptions = [...new Set((allTabClasses || []).map((c) => c.startDate?.slice(0, 10)).filter(Boolean))]
			.sort()
			.map((d) => `<option value="${d}" ${this._filters.startDate === d ? 'selected' : ''}>${_formatDate(d)}</option>`)
			.join('');
		filtersContainer.innerHTML = `
			<div class="u-flex u-flex-column md:u-flex-row u-gap-2 u-mb-3" role="search" aria-label="Filter classes">
				<select class="p-course-providers-course-details__filter-input" data-filter="startDate" aria-label="Filter by start date">
					<option value="">Any start date</option>
					${startDateOptions}
				</select>
				<input type="text" class="p-course-providers-course-details__filter-input" data-filter="venue" aria-label="Filter by venue" placeholder="Any venue" value="${this._filters.venue}" />
				<input type="text" class="p-course-providers-course-details__filter-input" data-filter="booked" aria-label="Filter by booked" placeholder="Booked" value="${this._filters.booked}" />
				<input type="text" class="p-course-providers-course-details__filter-input" data-filter="waiting" aria-label="Filter by waiting" placeholder="Waiting" value="${this._filters.waiting}" />
				<select class="p-course-providers-course-details__filter-input" data-filter="status" aria-label="Filter by status">
					<option value="">Any status</option>
					${statusOptions}
				</select>
			</div>`;

		filtersContainer.querySelectorAll('[data-filter]').forEach((input) => {
			const handler = () => {
				this._filters[input.dataset.filter] = input.value;
				this._updateTabTableUI()
			};
			input.addEventListener('input', handler);
			input.addEventListener('change', handler);
		});
	}

	_updateTabTableUI() {
		const tabContainer = this._wrapper.querySelector(TAB_SELECTORS[this._activeTab]);
		if (!tabContainer) return;

		const tableContainer = tabContainer.querySelector('.js-classes-table');
		const emptyMsg = tabContainer.querySelector('.js-classes-empty');
		if (!tableContainer) return;

		const allTabClasses = this._getTabClasses();
		const processedClasses = this._applySort(this._applyFilters(allTabClasses));

		if (!allTabClasses.length) {
			tableContainer.innerHTML = '';
			if (emptyMsg) emptyMsg.hidden = false;
			return;
		}
		if (emptyMsg) emptyMsg.hidden = true;

		const headers =
			TABLE_COLUMNS.map((col) =>
				col.sortable
					? `<th style="cursor:pointer" data-sort-key="${col.key}" aria-sort="${this._classesSort.key === col.key && this._classesSort.dir === 1 ? 'ascending' : this._classesSort.key === col.key && this._classesSort.dir === 2 ? 'descending' : 'none'}">${col.label}${this._sortIndicator(col.key)}</th>`
					: `<th>${col.label}</th>`
			).join('');

		const rows = processedClasses.map((c) => `
			<tr style="cursor:pointer" data-class-id="${c.id}">
				<td style="min-width: 125px;"><a class='u-p1' href="${this._classDetailsPageUrl}?classId=${c.id}">${_formatDate(c.startDate)}</a></td>
				<td>${c.venueName || ''}</td>
				<td style="min-width: 120px;">${c.totalBooked}</td>
				<td style="min-width: 120px;">${c.totalWaiting}</td>
				<td><div class="lta-tag ${_statusTagClass(c.status)}">${c.status || ''}</div></td>
			</tr>`
		).join('');

		tableContainer.innerHTML = `
			<div class="p-course-providers-course-details__details-table">
				<table>
					<thead><tr>${headers}</tr></thead>
					<tbody>${rows}</tbody>
				</table>
			</div>
		`;

		tableContainer.querySelectorAll('th[data-sort-key]').forEach((th) => {
			th.addEventListener('click', () => {
				const k = th.dataset.sortKey;
				const nextDir = this._classesSort.key === k ? (this._classesSort.dir + 1) % 3 : 1;
				this._classesSort = { key: nextDir === 0 ? null : k, dir: nextDir };
				this._updateTabTableUI()
			});
		});
		tableContainer.querySelectorAll('tr[data-class-id]').forEach((row) => {
			const navigate = () => { window.location.href = `${this._classDetailsPageUrl}?classId=${row.dataset.classId}`; };
			row.addEventListener('click', navigate);
		});
	}

	_sortIndicator(key) {
		if (this._classesSort.key !== key || this._classesSort.dir === 0) return '';
		return this._classesSort.dir === 1 ? ' ▲' : ' ▼';
	}

	_getTabClasses() {
		const classes = this._classes || [];
		const fn = CLASS_FILTER[this._activeTab];
		return fn ? classes.filter(fn) : [...classes];
	}

	_applyFilters(classes) {
		const { startDate, venue, booked, waiting, status } = this._filters;
		if (!startDate && !venue && !booked && !waiting && !status) return classes;
		return classes.filter((c) => {
			if (startDate && c.startDate && !c.startDate.startsWith(startDate)) return false;
			if (venue && !(c.venueName || '').toLowerCase().includes(venue.toLowerCase())) return false;
			if (booked && !String(c.totalBooked).includes(booked)) return false;
			if (waiting && !String(c.totalWaiting).includes(waiting)) return false;
			if (status && c.status !== status) return false;
			return true;
		});
	}

	_applySort(classes) {
		const { key, dir } = this._classesSort;
		if (!key || dir === 0) return classes;
		return [...classes].sort((a, b) => {
			const va = a[key] ?? '';
			const vb = b[key] ?? '';
			const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
			return dir === 1 ? cmp : -cmp;
		});
	}

	async _fetchCourseDetails() {
		try {
			const response = await Request.get(this._courseDetailsApiUrl, { courseid: this._courseID });
			this._currentCourse = response?.responseContainerContents?.courses[0] || {};
			this._classes = response?.responseContainerContents?.classes || [];
		} catch (error) {
			this._currentCourse = {};
			this._classes = [];
		} finally {
			this._wrapperLoading.hidden = true;
			const hasData = Object.keys(this._currentCourse).length > 0;
			this._courseDetailsContainer.hidden = !hasData;
			this._wrapperEmpty.hidden = hasData;
			if (hasData) {
				this._updateCourseInfoUI();
				this._updateTabCountsUI();
				this._updateFiltersUI();
				this._updateTabTableUI()
			}
		}
	}
}

const globalCourseProvidersCourseDetails = new CourseProvidersCourseDetails();
export default globalCourseProvidersCourseDetails;
